import {useCallback, useEffect, useState, useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {repositoryPath} from '@github-ui/paths'
import {COPILOT_REPOSITORIES_API_ENDPOINT} from '../utils/constants'

export interface DropdownRepository {
  id: number
  name: string // This will be nameWithOwner for display in ReposSelector
  nameOnly: string // This will be the original repository name (without owner)
  nameWithOwner: string
  owner: string
  enabled: boolean
  defaultBranch: string
  private: boolean
}

export interface SimpleRepository {
  databaseId?: number
  name: string
  defaultBranch?: string // Optional default branch if available from repository data
  owner: {
    login: string
  }
  isPrivate?: boolean
}

/**
 * Fetch default branch for a specific repository using the GitHub repository path
 * This is more efficient than using the search API since it targets a specific repo directly
 */
export async function fetchRepositoryDefaultBranch(nameWithOwner: string): Promise<string | null> {
  try {
    const [owner, repo] = nameWithOwner.split('/')
    if (!owner || !repo) {
      return null
    }

    // Use the standard repositoryPath function and append a repo info endpoint
    const url = repositoryPath({owner, repo})
    const response = await verifiedFetchJSON(url)

    if (response.ok) {
      const repoData = await response.json()
      return repoData?.payload.repo.defaultBranch || null
    }

    return null
  } catch {
    // Silently fail if fetching default branch fails, we'll get the branch name
    // From the selected repository's defaultBranch property if available
    return null
  }
}

/**
 * Hook to fetch repository default branch with React Query caching
 * This provides better caching, deduplication, and loading states
 */
export function useRepositoryDefaultBranch(nameWithOwner: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['repository-default-branch', nameWithOwner],
    queryFn: () => fetchRepositoryDefaultBranch(nameWithOwner ?? ''),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Only retry once on failure
  })
}

/**
 * Shared function to fetch Copilot-enabled repositories from the API
 */
export async function queryForRepos(query: string = ''): Promise<DropdownRepository[]> {
  const url = query.trim()
    ? `${COPILOT_REPOSITORIES_API_ENDPOINT}?q=${encodeURIComponent(query.trim())}`
    : COPILOT_REPOSITORIES_API_ENDPOINT
  const response = await verifiedFetchJSON(url)
  if (response.ok) {
    let repos = (await response.json()).repositories.map(
      (repo: {
        id: number
        nameWithOwner: string
        enabled: boolean
        name: string
        owner: string
        defaultBranch: string
        visibility: string
      }) => {
        return {
          id: repo.id,
          nameWithOwner: repo.nameWithOwner,
          enabled: repo.enabled,
          name: repo.nameWithOwner,
          nameOnly: repo.name,
          owner: repo.owner,
          defaultBranch: repo.defaultBranch,
          private: repo.visibility !== 'public',
        }
      },
    )

    // Apply client-side filtering to search both repository name and nameWithOwner
    if (query.trim()) {
      const lowerQuery = query.toLowerCase()
      repos = repos.filter(
        (repo: DropdownRepository) =>
          repo.nameWithOwner.toLowerCase().includes(lowerQuery) || repo.nameOnly.toLowerCase().includes(lowerQuery),
      )
    }

    return repos
  }
  return []
}

/**
 * Hook to manage repository fetching and selection logic for Copilot assignment forms
 */
export function useCopilotRepositoryFetching(initialRepo: SimpleRepository) {
  // Memoize the initialRepo to prevent unnecessary re-renders when parent passes unstable object references
  const stableInitialRepo = useMemo(
    () => ({
      databaseId: initialRepo.databaseId,
      name: initialRepo.name,
      defaultBranch: initialRepo.defaultBranch,
      owner: {login: initialRepo.owner.login},
      isPrivate: initialRepo.isPrivate,
    }),
    [
      initialRepo.databaseId,
      initialRepo.name,
      initialRepo.defaultBranch,
      initialRepo.owner.login,
      initialRepo.isPrivate,
    ],
  )

  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  const [isLoadingBranch, setIsLoadingBranch] = useState(false)

  // Use React Query to fetch default branch with caching
  const nameWithOwner = `${stableInitialRepo.owner.login}/${stableInitialRepo.name}`
  const shouldFetchDefaultBranch = !stableInitialRepo.defaultBranch
  const {data: defaultBranchFromQuery, isLoading: isLoadingFromQuery} = useRepositoryDefaultBranch(
    nameWithOwner,
    shouldFetchDefaultBranch,
  )

  // Initialize repo from props — only if user hasn't picked a repo yet
  useEffect(() => {
    if (selectedRepo) return

    const repoNameWithOwner = `${stableInitialRepo.owner.login}/${stableInitialRepo.name}`

    // Set the repo immediately
    setSelectedRepo({
      id: stableInitialRepo.databaseId || 0,
      owner: stableInitialRepo.owner.login,
      name: repoNameWithOwner,
      nameOnly: stableInitialRepo.name,
      nameWithOwner: repoNameWithOwner,
      enabled: true,
      defaultBranch: stableInitialRepo.defaultBranch || '',
      private: stableInitialRepo.isPrivate || false,
    })

    // Set the branch immediately if we have defaultBranch from GraphQL data
    if (stableInitialRepo.defaultBranch) {
      setSelectedBranch(stableInitialRepo.defaultBranch)
    }
  }, [stableInitialRepo, selectedRepo])

  // Update loading state from React Query
  useEffect(() => {
    setIsLoadingBranch(isLoadingFromQuery)
  }, [isLoadingFromQuery])

  // Update branch when React Query returns data
  useEffect(() => {
    if (defaultBranchFromQuery && !stableInitialRepo.defaultBranch) {
      setSelectedBranch(defaultBranchFromQuery)
      setSelectedRepo(prev => (prev ? {...prev, defaultBranch: defaultBranchFromQuery} : prev))
    }
  }, [defaultBranchFromQuery, stableInitialRepo.defaultBranch])

  const handleRepoSelect = useCallback((repo: DropdownRepository | undefined) => {
    if (!repo) return

    // Reset loading state since we're setting the branch immediately from repo.defaultBranch
    setIsLoadingBranch(false)
    setSelectedRepo(repo)
    setSelectedBranch(repo.defaultBranch)
  }, [])

  return {
    selectedRepo,
    selectedBranch,
    isLoadingBranch,
    setSelectedBranch,
    handleRepoSelect,
  }
}
